import React, { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ROUTE_DEFINITIONS } from '@/routes';
import type { TableColumn , ExpandableConfig } from '@/components/ui/tables/Table.types';
import { AsyncJobExpandedRow } from '@/components/features/asyncJob/AsyncJobExpandedRow';
import { ProgressBar } from '@/components/ui/loading';
import { ActionMenu } from '@/components/ui/menus';
import type { ActionMenuItem } from '@/components/ui/menus';
import { asyncJobService } from '@/api/domains/asyncJob/asyncJob.service';
import { mapAsyncJobs } from '@/api/domains/asyncJob/asyncJob.mapper';
import type { AsyncJob, AsyncJobEffectiveStatus, AsyncJobFilters, LC_AsyncJobTypeOut } from '@/api/domains/asyncJob/types';
import { useAsyncJobProcesses } from '@/hooks/asyncJob/useAsyncJobProcesses';
import type { AsyncJobProcess } from '@/api/domains/catalog/asyncJobProcess/asyncJobProcess.types';
import { useUI } from '@/hooks/ui/useUI';
import { usePaginatedList } from '@/hooks/ui/usePaginatedList';
import type { PaginatedFetchParams, PaginatedFetchResult } from '@/hooks/ui/usePaginatedList';
import { useCatalogLoader } from '@/hooks/ui/useCatalogLoader';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import { formatDateToYYYYMMDD, downloadBlob } from '@/utils';
import { colors } from '@/assets/styles/colors';


// ── Types ──────────────────────────────────────────────────────────────────

export interface UseAsyncJobListResult {
  rows: AsyncJob[];
  total: number;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  loading: boolean;
  isInitializing: boolean;
  columns: TableColumn<AsyncJob>[];
  expandable: ExpandableConfig<AsyncJob>;
  onSearch: (term: string) => void;
  onSortChange: (key: string, direction: 'asc' | 'desc') => void;
  filters: AsyncJobFilters;
  handleDateRangeChange: (range: { from?: string | null; to?: string | null }) => void;
  handleProcessChange: (value: string | string[]) => void;
  handleJobTypeChange: (value: string | string[]) => void;
  processOptions: { value: string; label: string }[];
  jobTypeOptions: { value: string; label: string }[];
  refetch: () => void;
  jobTypes: LC_AsyncJobTypeOut[];
  asyncJobProcesses: AsyncJobProcess[];
}

// ── Status color mapping ───────────────────────────────────────────────────

const STATUS_COLOR_MAP: Record<AsyncJobEffectiveStatus, string> = {
  in_progress: colors.important.main,
  completed: colors.feedback.success300,
  failed: colors.feedback.error300,
  pending: colors.feedback.alert300,
  stopped: colors.grays.neutral66,
  expired: colors.grays.neutral99,
};

// ── Helpers ────────────────────────────────────────────────────────────────

function canShowDownload(row: AsyncJob): boolean {
  if (row.effectiveStatus === 'expired') return false;
  if (row.category === 'execution') return false;
  if (row.category === 'export') return row.effectiveStatus === 'completed';
  // import: always show download when file is available
  return true;
}

// ── Constants ──────────────────────────────────────────────────────────────

const MAX_DATE_RANGE_DAYS = 30;

// ── Initial filter state ───────────────────────────────────────────────────

const buildInitialFilters = (): AsyncJobFilters => {
  const today = new Date();
  const thirtyDaysAgo = new Date(today);
  thirtyDaysAgo.setDate(today.getDate() - 30);
  return {
    dateFrom: formatDateToYYYYMMDD(thirtyDaysAgo),
    dateTo: formatDateToYYYYMMDD(today),
    jobType: null,
    process: null,
  };
};

// ── Hook ───────────────────────────────────────────────────────────────────

export function useAsyncJobList(): UseAsyncJobListResult {
  const { t: translate } = useTranslation(['asyncJob', 'common']);
  const { showApiError, showNotification } = useUI();
  const navigate = useNavigate();
  const { canView, isLoaded: permissionsLoaded } = usePagePermissions();

  // ── Permission guard ──────────────────────────────────────────────────
  useEffect(() => {
    if (permissionsLoaded && !canView) {
      void navigate(ROUTE_DEFINITIONS.DASHBOARD_ASISTENCIA.path);
    }
  }, [permissionsLoaded, canView, navigate]);


  // ── Load async job processes catalog ──────────────────────────────────
  const { asyncJobProcesses, isLoaded: processesLoaded } = useAsyncJobProcesses();

  // ── Load job types catalog ─────────────────────────────────────────────
  const typesCatalog = useCatalogLoader<LC_AsyncJobTypeOut>(
    (signal) => asyncJobService.getTypes(signal),
    (item) => ({ value: item.jobType, label: item.label }),
    [],
    false,
    () => { showNotification('error', translate('asyncJob:messages.loadError')); },
  );

  const typesLoaded = typesCatalog.isLoaded;
  const jobTypes = typesCatalog.data;

  // ── Build lookup maps from types ───────────────────────────────────────
  const typeMap = useMemo(
    () =>
      jobTypes.reduce<Record<string, 'export' | 'import' | 'execution'>>((accumulator, type) => {
        accumulator[type.jobType] = type.category;
        return accumulator;
      }, {}),
    [jobTypes],
  );

  const typeLabels = useMemo(
    () =>
      jobTypes.reduce<Record<string, string>>((accumulator, type) => {
        accumulator[type.jobType] = type.label;
        return accumulator;
      }, {}),
    [jobTypes],
  );

  // ── Fetch function ────────────────────────────────────────────────────
  const fetchFn = useCallback(
    async (
      params: PaginatedFetchParams<AsyncJobFilters>,
      signal: AbortSignal,
    ): Promise<PaginatedFetchResult<AsyncJob> | null> => {
      const response = await asyncJobService.listJobs(
        {
          page: params.page,
          pageSize: params.pageSize,
          searchText: params.searchText,
          orderBy: params.sort?.key,
          orderDirection: params.sort?.direction,
          dateFrom: params.filters.dateFrom ?? undefined,
          dateTo: params.filters.dateTo ?? undefined,
          jobType: params.filters.jobType ?? undefined,
          process: params.filters.process ?? undefined,
        },
        signal,
      );

      if (!response.success || !response.data) {
        if (response.error?.code !== 'ABORTED') {
          showApiError(response, translate('asyncJob:messages.loadError'));
        }
        return null;
      }

      const mappedItems = mapAsyncJobs(response.data.data ?? [], typeMap, typeLabels);

      return {
        items: mappedItems,
        totalRecords: response.data.pagination?.totalRecords ?? 0,
      };
    },
    [showApiError, translate, typeMap, typeLabels],
  );

  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading,
    onSearch,
    onSortChange,
    filters,
    setFilters,
    refetch,
  } = usePaginatedList<AsyncJob, AsyncJobFilters>({
    fetchFn,
    initialFilters: buildInitialFilters(),
    initialSort: { key: 'createdAt', direction: 'DESC' },
    enabled: permissionsLoaded && canView && typesLoaded && processesLoaded,
    orderByMap: {
      jobTypeLabel: 'jobType',
      effectiveStatus: 'status',
    },
    screenCode: ROUTE_DEFINITIONS.PROCESOS_ASINCRONOS.code,
  });

  // ── Date range change handler ─────────────────────────────────────────
  const handleDateRangeChange = useCallback(
    (range: { from?: string | null; to?: string | null }) => {
      const dateFrom = range.from ?? null;
      const dateTo = range.to ?? null;

      if (dateFrom && dateTo) {
        const fromMs = new Date(dateFrom.replace(/\//g, '-')).getTime();
        const toMs = new Date(dateTo.replace(/\//g, '-')).getTime();
        const diffDays = Math.round((toMs - fromMs) / (1000 * 60 * 60 * 24));
        if (diffDays > MAX_DATE_RANGE_DAYS) {
          showNotification('error', translate('asyncJob:filters.dateRangeExceeds'));
          return;
        }
      }

      setFilters((previous) => ({ ...previous, dateFrom, dateTo }));
    },
    [showNotification, translate, setFilters],
  );

  // ── Filter change handlers ────────────────────────────────────────────
  const handleProcessChange = useCallback((value: string | string[]) => {
    const stringValue = Array.isArray(value) ? value[0] : value;
    setFilters((previous) => ({ ...previous, process: stringValue === '' ? null : stringValue }));
  }, [setFilters]);

  const handleJobTypeChange = useCallback((value: string | string[]) => {
    const stringValue = Array.isArray(value) ? value[0] : value;
    setFilters((previous) => ({ ...previous, jobType: stringValue === '' ? null : stringValue }));
  }, [setFilters]);

  // ── Catalog options ───────────────────────────────────────────────────
  const processOptions = useMemo(
    () => [
      { value: '', label: translate('asyncJob:filters.all') },
      ...asyncJobProcesses.map((item) => ({ value: item.code, label: item.label })),
    ],
    [asyncJobProcesses, translate],
  );

  const jobTypeOptions = useMemo(
    () => [
      { value: '', label: translate('asyncJob:filters.all') },
      ...jobTypes.map((item) => ({ value: item.jobType, label: item.label })),
    ],
    [jobTypes, translate],
  );

  // ── Download handler ──────────────────────────────────────────────────
  const handleDownload = useCallback(async (row: AsyncJob) => {
    const response = await asyncJobService.downloadJob(row.id);
    if (!response.success || !response.data) {
      showApiError(response, translate('asyncJob:messages.loadError'));
      return;
    }
    downloadBlob(response.data.blob, response.data.filename);
  }, [showApiError, translate]);

  // ── Columns ──────────────────────────────────────────────────────────
  const columns: TableColumn<AsyncJob>[] = useMemo(() => [
    {
      key: 'jobTypeLabel',
      header: translate('asyncJob:table.jobType'),
      sortable: true,
    },
    {
      key: 'requesterName',
      header: translate('asyncJob:table.requesterName'),
      sortable: true,
    },
    {
      key: 'createdAt',
      header: translate('asyncJob:table.createdAt'),
      sortable: true,
    },
    {
      key: 'effectiveStatus',
      header: translate('asyncJob:table.status'),
      sortable: true,
      render: (row: AsyncJob) => (
        React.createElement('span', {
          style: { color: STATUS_COLOR_MAP[row.effectiveStatus], fontWeight: 500 },
        }, translate(`asyncJob:status.${row.effectiveStatus}`))
      ),
    },
    {
      key: 'progress',
      header: translate('asyncJob:table.progress'),
      sortable: false,
      render: (row: AsyncJob) => {
        const isFinished = ['completed', 'failed', 'stopped', 'expired'].includes(row.effectiveStatus);

        if (!isFinished) {
          return React.createElement(ProgressBar, { value: row.progress });
        }

        if (!canShowDownload(row)) return null;

        const menuItems: ActionMenuItem[] = [
          {
            id: 'download',
            label: translate('common:tableActions.download'),
            icon: 'import',
            onClick: () => { void handleDownload(row); },
          },
        ];
        return React.createElement(ActionMenu, { items: menuItems });
      },
    },
  ], [translate, handleDownload]);

  // ── Expandable rows ───────────────────────────────────────────────────
  const renderExpanded = useCallback(
    (row: AsyncJob) => React.createElement(AsyncJobExpandedRow, { row }),
    [],
  );

  const rowExpandable = useCallback((_row: AsyncJob) => true, []);

  const expandable = useMemo<ExpandableConfig<AsyncJob>>(() => ({
    showExpandColumn: true,
    accordion: true,
    renderExpanded,
    rowExpandable,
  }), [renderExpanded, rowExpandable]);

  return {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading,
    isInitializing: !typesLoaded || !permissionsLoaded || !processesLoaded,
    columns,
    expandable,
    onSearch,
    onSortChange,
    filters,
    handleDateRangeChange,
    handleProcessChange,
    handleJobTypeChange,
    processOptions,
    jobTypeOptions,
    refetch,
    jobTypes,
    asyncJobProcesses,
  };
}

export default useAsyncJobList;
